import _                                                       from 'underscore';
import classNames                                              from 'classnames';
import { v4 as uuid }                                          from 'uuid';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Header, Icon, IconProps, SemanticICONS }              from 'semantic-ui-react';

import { CONSTANTS }              from '../../../constants';
import { CONTEXTS }               from '../../../constants/ContextEnums';
import { Flexbox, FlexItem }      from '../../../views/containers';
import { promisedTimeout }        from '../../../util';
import { FlexAlign, FlexJustify } from '../../../types';
import { useSystemToasts }        from '../../../hooks';
import { Cell, Grid }             from 'styled-css-grid';

export interface IToast {
	id?: string,
	type?: CONTEXTS,
	title?: string,
	message?: string,
	timeoutMS?: number,
	dismissable?: boolean,
	onDismiss?: () => void,
	icon?: IconProps,
}

interface ToastCardProps {
	item: IToast
}

export const defaultProps: ToastCardProps = {
	item: {
		id:          uuid(),
		type:        CONTEXTS.INFO,
		title:       '[Title]',
		message:     '',
		timeoutMS:   CONSTANTS.UI.DISPLAY.WAIT_SHORT,
		dismissable: true,
		onDismiss:   null,
	}
}

const ToastCard: FC<ToastCardProps> = ( props ) => {
	
	let { id, type, title, message, timeoutMS, dismissable, onDismiss, icon } = { ...defaultProps.item, ...props.item };
	
	let toastActions                    = useSystemToasts(),
	    [ hiding, setIsHiding ]         = useState( false ),
	    [ timeout, setTimeoutAssigned ] = useState( null );
	
	// Keep track of our component's mounted state w/ a ref
	// https://daviseford.com/blog/2019/07/11/react-hooks-check-if-mounted.html
	// ------------------------------------------------------------------------------------------
	const componentIsMounted = useRef( true );
	
	// Actually remove this toast, with a nice fade out
	// ------------------------------------------------------------------------------------------
	const lazilyRemoveToast = useCallback( async () => {
		
		try{
			
			if( componentIsMounted.current ){
				
				// set to fade out
				setIsHiding( true );
				
				await promisedTimeout( CONSTANTS.UI.DISPLAY.ANIMATION_DELAY );
				
				toastActions.removeById( id );
			}
			
		} catch( e ) {
			
			// e...
			
		} finally {
			
			if( _.isFunction( onDismiss ) ){
				onDismiss();
			}
		}
		
	}, [ setIsHiding, toastActions, id ] );
	
	// Set timeout on mount
	// ------------------------------------------------------------------------------------------
	useEffect( () => {
		
		// Set timeout if desired
		if( timeoutMS > 0 ){
			setTimeoutAssigned( setTimeout( lazilyRemoveToast, timeoutMS ) );
		}
		
		// on unmount, set unmounted flag
		return () => {
			componentIsMounted.current = false
		}
	}, [] );
	
	// Classes
	// ------------------------------------------------------------------------------------------
	const classes = classNames( [
		'ui',
		'toast',
		`toast--${ type }`,
	], {
		'toast--in':       !hiding,
		'toast--out':      hiding,
		'cursor--pointer': dismissable,
	} );
	
	// Toast Icons
	// ------------------------------------------------------------------------------------------
	const iconMap: Record<CONTEXTS, SemanticICONS> = {
		[ CONTEXTS.INFO ]:    'info circle',
		[ CONTEXTS.SUCCESS ]: 'checkmark',
		[ CONTEXTS.WARNING ]: 'warning sign',
		[ CONTEXTS.ERROR ]:   'exclamation circle',
	};
	
	// Header Classes
	// ------------------------------------------------------------------------------------------
	const headerContainerClasses = classNames( {
		'padding-sm-bottom': !!( message ),
		'display--inline':   true,
	} );
	
	let toastMessage = _.isFunction( message ) ? message( id ) : message,
	    iconProps    = ( icon ?? { name: iconMap[ type ] } ) as IconProps,
	    handleClick  = dismissable ? lazilyRemoveToast : null;
	
	return (
		<div key={ id } className={ classes } onClick={ handleClick }>
			
			<Grid areas={ [ 'icon title', '. message' ] } rowGap={ '0px' } columns={ `auto 1fr` } rows={ `auto auto` } className={ headerContainerClasses }>
				
				<Cell area={ 'icon' }>
					
					<Icon { ...iconProps }/>
				
				</Cell>
				<Cell area={ 'title' }>
					
					{ title && <Header as={ 'h5' } className={ 'no-margin text-left' }>{ title }</Header> }
				
				</Cell>
				<Cell area={ 'message' }>
					
					{ toastMessage && <div className={ 'content padding-xs-vertical' }>{ toastMessage }</div> }
				
				</Cell>
			
			</Grid>
			
			{/*{ title && (
				<Flexbox noWrap
				         justify={ FlexJustify.START }
				         align={ FlexAlign.CENTER }
				         className={ headerContainerClasses }>
					
					<FlexItem noGrow>
						<Icon { ...iconProps }/>
					</FlexItem>
					
					<FlexItem noGrow>
						<Header as={ 'h5' } className={ 'no-margin text-left' }>{ title }</Header>
					</FlexItem>
				
				</Flexbox>
			) }
			
			{ toastMessage && <div className={ 'content' }>{ toastMessage }</div> }*/ }
		
		</div>
	);
};

ToastCard.defaultProps = defaultProps;

export { ToastCard };
